import React, { useState, useEffect, useCallback } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import {
  getCustomers,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  getCountries,
  getCustomerCompanies,
  linkCustomerCompany,
  unlinkCustomerCompany,
  getCompaniesAll,
} from '../utils/api';
import Modal from '../components/Modal';
import CustomerContractsModal from '../components/CustomerContractsModal';
import CustomerProjectsChannelsModal from '../components/CustomerProjectsChannelsModal';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';

const CustomerManagement = () => {
  usePageTitle('Customer Management');

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [countries, setCountries] = useState([]);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [contractsModalOpen, setContractsModalOpen] = useState(false);
  const [selectedCustomerForContracts, setSelectedCustomerForContracts] = useState(null);
  const [projectsChannelsModalOpen, setProjectsChannelsModalOpen] = useState(false);
  const [selectedCustomerForProjectsChannels, setSelectedCustomerForProjectsChannels] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    customer_id: '',
    address: '',
    country: '',
    vat_number: '',
  });
  const [saving, setSaving] = useState(false);

  // Linked companies state (for edit modal)
  const [allCompanies, setAllCompanies] = useState([]);
  const [linkedCompanies, setLinkedCompanies] = useState([]);
  const [selectedCompanyToLink, setSelectedCompanyToLink] = useState('');
  const [linkingCompany, setLinkingCompany] = useState(false);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCustomers({ search, page: pagination.page, limit: 20 });
      setCustomers(response.data.customers);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Error loading customers');
    } finally {
      setLoading(false);
    }
  }, [search, pagination.page]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [countriesRes, companiesRes] = await Promise.all([
          getCountries(),
          getCompaniesAll(),
        ]);
        setCountries(countriesRes.data);
        setAllCompanies(companiesRes.data.companies || companiesRes.data);
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };
    loadInitialData();
  }, []);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const openModal = async (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        name: customer.name || '',
        customer_id: customer.customer_id || '',
        address: customer.address || '',
        country: customer.country || '',
        vat_number: customer.vat_number || '',
      });
      // Load linked companies for this customer
      try {
        const response = await getCustomerCompanies(customer.id);
        setLinkedCompanies(response.data);
      } catch (error) {
        console.error('Error loading linked companies:', error);
        setLinkedCompanies([]);
      }
    } else {
      setEditingCustomer(null);
      setFormData({
        name: '',
        customer_id: '',
        address: '',
        country: '',
        vat_number: '',
      });
      setLinkedCompanies([]);
    }
    setSelectedCompanyToLink('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingCustomer(null);
    setLinkedCompanies([]);
    setSelectedCompanyToLink('');
  };

  const handleLinkCompany = async () => {
    if (!selectedCompanyToLink || !editingCustomer) return;

    setLinkingCompany(true);
    try {
      await linkCustomerCompany(editingCustomer.id, selectedCompanyToLink);
      const response = await getCustomerCompanies(editingCustomer.id);
      setLinkedCompanies(response.data);
      setSelectedCompanyToLink('');
      toast.success('Company linked successfully');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error linking company');
    } finally {
      setLinkingCompany(false);
    }
  };

  const handleUnlinkCompany = async (companyId, companyName) => {
    if (!window.confirm(`Are you sure you want to unlink "${companyName}"?`)) {
      return;
    }

    try {
      await unlinkCustomerCompany(editingCustomer.id, companyId);
      const response = await getCustomerCompanies(editingCustomer.id);
      setLinkedCompanies(response.data);
      toast.success('Company unlinked successfully');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error unlinking company');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Customer name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingCustomer) {
        await updateCustomer(editingCustomer.id, formData);
        toast.success('Customer updated successfully');
      } else {
        await createCustomer(formData);
        toast.success('Customer created successfully');
      }
      closeModal();
      loadCustomers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error saving customer');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (customer) => {
    if (!window.confirm(`Are you sure you want to delete "${customer.name}"?`)) {
      return;
    }

    try {
      await deleteCustomer(customer.id);
      toast.success('Customer deleted successfully');
      loadCustomers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error deleting customer');
    }
  };

  const openContractsModal = (customer) => {
    setSelectedCustomerForContracts(customer);
    setContractsModalOpen(true);
  };

  const closeContractsModal = () => {
    setContractsModalOpen(false);
    setSelectedCustomerForContracts(null);
  };

  const openProjectsChannelsModal = (customer) => {
    setSelectedCustomerForProjectsChannels(customer);
    setProjectsChannelsModalOpen(true);
  };

  const closeProjectsChannelsModal = () => {
    setProjectsChannelsModalOpen(false);
    setSelectedCustomerForProjectsChannels(null);
  };

  // Get companies that are not already linked
  const availableCompanies = allCompanies.filter(
    (c) => !linkedCompanies.some((lc) => lc.id === c.id)
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Customer Management</h1>
          <p className="page-subtitle">{pagination.total} customers total</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <PlusIcon style={{ width: 18, height: 18 }} />
          Add Customer
        </button>
      </div>

      <div className="card">
        <div className="search-bar">
          <div className="search-input-wrapper">
            <MagnifyingGlassIcon className="search-icon" style={{ width: 18, height: 18 }} />
            <input
              type="text"
              className="form-input search-input"
              placeholder="Search customers by name, ID, VAT, or country..."
              value={search}
              onChange={handleSearch}
            />
          </div>
        </div>

        {loading ? (
          <div className="loading-overlay">
            <div className="spinner" />
            <span>Loading customers...</span>
          </div>
        ) : customers.length > 0 ? (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Company ID</th>
                    <th>Company Name</th>
                    <th>Country</th>
                    <th>VAT ID</th>
                    <th>Address</th>
                    <th style={{ width: 140 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => (
                    <tr key={customer.id}>
                      <td className="font-mono">{customer.customer_id}</td>
                      <td><strong>{customer.name}</strong></td>
                      <td>{customer.country || '-'}</td>
                      <td className="font-mono">{customer.vat_number || '-'}</td>
                      <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {customer.address || '-'}
                      </td>
                      <td>
                        <div className="table-actions">
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={() => openProjectsChannelsModal(customer)}
                            title="Products & Channels"
                          >
                            <SignalIcon style={{ width: 16, height: 16 }} />
                          </button>
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={() => openContractsModal(customer)}
                            title="Contracts"
                          >
                            <DocumentTextIcon style={{ width: 16, height: 16 }} />
                          </button>
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={() => openModal(customer)}
                            title="Edit"
                          >
                            <PencilIcon style={{ width: 16, height: 16 }} />
                          </button>
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={() => handleDelete(customer)}
                            title="Delete"
                            style={{ color: 'var(--color-accent-danger)' }}
                          >
                            <TrashIcon style={{ width: 16, height: 16 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="pagination">
                <span className="pagination-info">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} items)
                </span>
                <button
                  className="pagination-btn"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                >
                  ‹
                </button>
                {(() => {
                  const pages = [];
                  const start = Math.max(1, pagination.page - 2);
                  const end = Math.min(pagination.totalPages, start + 4);
                  for (let i = start; i <= end; i++) pages.push(i);
                  return pages.map((pageNum) => (
                    <button
                      key={pageNum}
                      className={`pagination-btn ${pagination.page === pageNum ? 'active' : ''}`}
                      onClick={() => setPagination((prev) => ({ ...prev, page: pageNum }))}
                    >
                      {pageNum}
                    </button>
                  ));
                })()}
                <button
                  className="pagination-btn"
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                >
                  ›
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <UserGroupIcon className="empty-state-icon" />
            <p className="empty-state-title">No customers found</p>
            <p>{search ? 'Try adjusting your search' : 'Add your first customer to get started'}</p>
            {!search && (
              <button className="btn btn-primary" onClick={() => openModal()} style={{ marginTop: '1rem' }}>
                <PlusIcon style={{ width: 18, height: 18 }} />
                Add Customer
              </button>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingCustomer ? 'Edit Customer' : 'Add New Customer'}
        size={editingCustomer ? 'lg' : 'md'}
        footer={
          <>
            <button className="btn btn-secondary" onClick={closeModal}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : editingCustomer ? 'Update Customer' : 'Create Customer'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Company Name *</label>
            <input
              type="text"
              className="form-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter company name"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Company ID</label>
              <input
                type="text"
                className="form-input"
                value={formData.customer_id}
                onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                placeholder="Auto-generated if empty"
              />
            </div>
            <div className="form-group">
              <label className="form-label">VAT ID</label>
              <input
                type="text"
                className="form-input"
                value={formData.vat_number}
                onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                placeholder="e.g., DE123456789"
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Country</label>
            <select
              className="form-select"
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
            >
              <option value="">Select country</option>
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <textarea
              className="form-textarea"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Enter full address"
              rows={3}
            />
          </div>
        </form>

        {/* Linked Companies Section - Only show when editing */}
        {editingCustomer && (
          <div style={{ marginTop: '1.5rem', borderTop: '1px solid var(--color-border)', paddingTop: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <BuildingOfficeIcon style={{ width: 20, height: 20 }} />
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Linked Companies</h3>
            </div>

            {/* Add Company Row */}
            <div className="form-row" style={{ alignItems: 'flex-end', marginBottom: '1rem' }}>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <select
                  className="form-select"
                  value={selectedCompanyToLink}
                  onChange={(e) => setSelectedCompanyToLink(e.target.value)}
                  disabled={availableCompanies.length === 0}
                >
                  <option value="">
                    {availableCompanies.length === 0
                      ? 'All companies already linked'
                      : 'Select a company to link...'}
                  </option>
                  {availableCompanies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name} ({company.code})
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleLinkCompany}
                disabled={!selectedCompanyToLink || linkingCompany}
                style={{ marginLeft: '0.5rem' }}
              >
                <PlusIcon style={{ width: 18, height: 18 }} />
                {linkingCompany ? 'Linking...' : 'Link'}
              </button>
            </div>

            {/* Linked Companies List */}
            {linkedCompanies.length > 0 ? (
              <div className="table-container" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                <table className="table" style={{ fontSize: '0.9rem' }}>
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Code</th>
                      <th style={{ width: 60 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {linkedCompanies.map((company) => (
                      <tr key={company.id}>
                        <td><strong>{company.name}</strong></td>
                        <td className="font-mono">{company.code}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={() => handleUnlinkCompany(company.id, company.name)}
                            title="Unlink"
                            style={{ color: 'var(--color-accent-danger)' }}
                          >
                            <TrashIcon style={{ width: 16, height: 16 }} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--color-text-muted)' }}>
                No companies linked yet
              </div>
            )}
          </div>
        )}
      </Modal>

      <CustomerContractsModal
        isOpen={contractsModalOpen}
        onClose={closeContractsModal}
        customer={selectedCustomerForContracts}
      />

      <CustomerProjectsChannelsModal
        isOpen={projectsChannelsModalOpen}
        onClose={closeProjectsChannelsModal}
        customer={selectedCustomerForProjectsChannels}
      />
    </div>
  );
};

export default CustomerManagement;
